import { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, Card, Col, Container, Dropdown, Form, Modal, Row, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "../../api/axios";
import { useFeedbackMessage } from "../Common/Feedback/FeedbackHub";
import { FeedSkeleton } from "../Common/Loading/PageSkeletons";
import { postTypeClass, postTypes } from "../../utils/feedPresentation";
import ActivationGuide from "../Common/ActivationGuide";

const feedFilters = [
  ["all", "Todo"],
  ["pull", "Pulls"],
  ["trade_completed", "Experiencias"],
  ["wanted_card", "Buscadas"],
  ["community_event", "Eventos"],
  ["store_post", "Tienda"],
  ["collection_update", "Binders"]
];

const avatarInitial = name => (name || "C").trim().slice(0, 1).toUpperCase();
const binderShareDetail = metadata => {
  const mode = metadata?.shareMode || "binder";
  const cardsCount = Number(metadata?.cardsCount || 0);
  const wantedCount = Number(metadata?.wantedCount || 0);
  const copiesCount = Number(metadata?.availableCopiesCount || 0);
  const targetTotal = Number(metadata?.targetTotal || 0);
  if (mode === "duplicates" || mode === "trade") return copiesCount ? `${copiesCount} ${copiesCount === 1 ? "carta disponible" : "cartas disponibles"}` : "Ver cartas para intercambio";
  if (mode === "wanted") return wantedCount ? `${wantedCount} ${wantedCount === 1 ? "carta buscada" : "cartas buscadas"}` : "Ver cartas buscadas";
  if (cardsCount && targetTotal) return `${cardsCount} de ${targetTotal} cartas`;
  if (cardsCount) return `${cardsCount} ${cardsCount === 1 ? "carta" : "cartas"}`;
  return "Explorar binder";
};

function FeedImagePreview({ file, onRemove }) {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div className="feed-image-preview">
      {previewUrl && <img src={previewUrl} alt={`Vista previa: ${file.name}`} />}
      <button type="button" onClick={onRemove} aria-label={`Quitar ${file.name}`}>
        <i className="bi bi-x-lg" />
      </button>
      <span>{file.name}</span>
    </div>
  );
}

function FeedHome({ isHome = false }) {
  const [posts, setPosts] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [form, setForm] = useState({ type: "status", body: "", images: [], collectionId: "", shareMode: "" });
  const imageInputRef = useRef(null);
  const [collections, setCollections] = useState([]);
  const [collectionCards, setCollectionCards] = useState([]);
  const [shareContentLoading, setShareContentLoading] = useState(false);
  const [imageViewer, setImageViewer] = useState(null);
  const [viewerImageLoaded, setViewerImageLoaded] = useState(false);
  const [comments, setComments] = useState({});
  const [messageTarget, setMessageTarget] = useState(null);
  const [privateMessage, setPrivateMessage] = useState({ subject: "", body: "" });
  const [notice, setNotice] = useState("");
  const [likedPostIds, setLikedPostIds] = useState(new Set());
  const [expandedCommentPostIds, setExpandedCommentPostIds] = useState(new Set());
  const [requestedUserIds, setRequestedUserIds] = useState(new Set());
  const [typeFilter, setTypeFilter] = useState("all");
  const [reportedPostIds, setReportedPostIds] = useState(new Set());
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [busyActions, setBusyActions] = useState(new Set());
  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const isBusy = key => busyActions.has(key);
  const setBusy = (key, active) => setBusyActions(current => { const next = new Set(current); if (active) next.add(key); else next.delete(key); return next; });

  const loadFeed = useCallback((silent = false) => {
    if (!silent) setFeedLoading(true);
    api
      .get("/feed", { params: { type: typeFilter !== "all" ? typeFilter : undefined } })
      .then(({ data }) => {
        setPosts(data);
        setLikedPostIds(new Set((data || []).filter(post => post.liked_by_me).map(post => post.id)));
      })
      .catch(() => setPosts([]))
      .finally(() => { if (!silent) setFeedLoading(false); });
  }, [typeFilter]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  useEffect(() => {
    if (!localStorage.getItem("token")) return;
    setShareContentLoading(true);
    Promise.allSettled([
      api.get("/collections/me"),
      api.get("/binder/me")
    ])
      .then(([collectionsResult, cardsResult]) => {
        const nextCollections = collectionsResult.status === "fulfilled" ? collectionsResult.value.data || [] : [];
        const nextCards = cardsResult.status === "fulfilled" ? cardsResult.value.data || [] : [];
        setCollections(nextCollections.filter(collection => collection.visibility === "public"));
        setCollectionCards(nextCards);
      })
      .finally(() => setShareContentLoading(false));
  }, []);

  const createPost = async event => {
    event.preventDefault();
    if (isBusy("publish")) return;
    setBusy("publish", true);
    const formData = new FormData();
    formData.append("type", form.type);
    formData.append("body", form.body);
    if (form.type === "collection_update") formData.append("metadata", JSON.stringify({ collectionId: form.collectionId, shareMode: form.shareMode }));
    form.images.slice(0, 2).forEach(file => formData.append("images", file));

    try {
      await api.post("/feed", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ type: "status", body: "", images: [], collectionId: "", shareMode: "" });
      if (imageInputRef.current) imageInputRef.current.value = "";
      event.target.reset();
      setNotice("Publicacion creada.");
      await loadFeed(true);
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo crear la publicacion.");
    } finally {
      setBusy("publish", false);
    }
  };

  const toggleComments = postId => {
    setExpandedCommentPostIds(current => {
      const next = new Set(current);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  };

  const likePost = async postId => {
    if (isBusy(`like:${postId}`)) return;
    if (!localStorage.getItem("token")) {
      setNotice("Necesitas iniciar sesion para dar like.");
      return;
    }

    setBusy(`like:${postId}`, true);
    const hasLiked = likedPostIds.has(postId);
    const nextLikedPostIds = new Set(likedPostIds);
    if (hasLiked) {
      nextLikedPostIds.delete(postId);
    } else {
      nextLikedPostIds.add(postId);
    }
    setLikedPostIds(nextLikedPostIds);
    setPosts(currentPosts => currentPosts.map(post => {
      if (post.id !== postId) return post;
      const nextLikesCount = Number(post.likes_count || 0) + (hasLiked ? -1 : 1);
      return { ...post, likes_count: Math.max(0, nextLikesCount) };
    }));

    try {
      if (hasLiked) {
        await api.delete(`/feed/${postId}/like`);
      } else {
        await api.post(`/feed/${postId}/like`);
      }
    } catch (error) {
      await loadFeed(true);
      setNotice("No pudimos actualizar el like.");
    } finally {
      setBusy(`like:${postId}`, false);
    }
  };

  const sendComment = async (event, postId) => {
    event.preventDefault();
    if (isBusy(`comment:${postId}`)) return;
    if (!localStorage.getItem("token")) {
      setNotice("Registrate para comentar en publicaciones.");
      return;
    }
    const body = comments[postId];
    if (!body) return;

    setBusy(`comment:${postId}`, true);
    try {
      await api.post(`/feed/${postId}/comments`, { body });
      setComments(current => ({ ...current, [postId]: "" }));
      await loadFeed(true);
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo enviar el comentario.");
    } finally {
      setBusy(`comment:${postId}`, false);
    }
  };

  const deletePost = async postId => {
    setDeletingPostId(postId);
    try {
      await api.delete("/feed/" + postId);
      setPendingDeleteId(null);
      setNotice("Publicacion eliminada.");
      await loadFeed(true);
    } finally {
      setDeletingPostId(null);
    }
  };

  const reportPost = async postId => {
    if (isBusy(`report:${postId}`)) return;
    setBusy(`report:${postId}`, true);
    try {
      await api.post(`/feed/${postId}/report`, { reason: "reported_from_feed" });
      setReportedPostIds(current => new Set(current).add(postId));
      setNotice("Reporte enviado.");
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo enviar el reporte.");
    } finally {
      setBusy(`report:${postId}`, false);
    }
  };

  const connectUser = async userId => {
    if (isBusy(`connect:${userId}`)) return;
    setBusy(`connect:${userId}`, true);
    try {
      const { data } = await api.post(`/social/users/${userId}/follow`);
      if (data.status === "accepted") {
        setPosts(current => current.map(post => post.user_id === userId ? { ...post, connection_status: "connected" } : post));
        setNotice("Ya existe una conexion con este usuario.");
      } else {
        setRequestedUserIds(current => new Set(current).add(userId));
        setPosts(current => current.map(post => post.user_id === userId ? { ...post, connection_status: "pending_outgoing" } : post));
        setNotice("Solicitud de conexion enviada.");
      }
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo enviar la solicitud.");
    } finally {
      setBusy(`connect:${userId}`, false);
    }
  };

  const commentTime = createdAt => {
    if (!createdAt) return "";
    return new Date(createdAt).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const openMessage = post => {
    setMessageTarget({ id: post.user_id, name: post.display_name });
    setPrivateMessage({ subject: "", body: "" });
    setNotice("");
  };

  const sendPrivateMessage = async event => {
    event.preventDefault();
    if (isBusy("message")) return;
    setBusy("message", true);
    try {
      await api.post("/messages", {
        receiverId: messageTarget.id,
        subject: privateMessage.subject,
        body: privateMessage.body
      });
      setNotice("Mensaje enviado.");
      setMessageTarget(null);
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo enviar el mensaje.");
    } finally {
      setBusy("message", false);
    }
  };

  const postLinks = post => {
    const metadata = post.metadata || {};
    const links = [];
    if (metadata.tournamentId) links.push(["Evento", `/tournaments/${metadata.tournamentId}`]);
    if (metadata.communityMeetupId) links.push(["Ver juntada", "/meetups"]);
    if (metadata.businessSlug || post.business_slug) links.push(["Tienda", `/store/${metadata.businessSlug || post.business_slug}`]);
    if (metadata.collectionId) {
      const shareMode = metadata.shareMode || "binder";
      const target = `/binder/${post.handle || post.user_id}/${metadata.collectionId}${shareMode === "binder" ? "" : `?view=${shareMode}`}`;
      const labels = { binder: "Ver binder", duplicates: "Ver copias", trade: "Ver intercambios", wanted: "Ver buscadas" };
      links.push([labels[shareMode] || metadata.collectionName || "Binder", target]);
    }
    if (metadata.tradeId) links.push(["Trade", "/trades"]);
    return links;
  };

  const cardsInCollection = collectionId => collectionCards.filter(card => card.collection_id === collectionId && !card.is_missing);
  const tradeQuantity = card => {
    if (!card || card.status === "wanted") return 0;
    const explicitlyAvailable = Math.max(0, Number(card.available_quantity || 0));
    const duplicateCopies = Math.max(0, Number(card.quantity || 1) - 1);
    const markedForTrade = card.status === "for_trade" || card.status === "for_sale" || card.card_intention === "for_trade";
    return Math.max(explicitlyAvailable, duplicateCopies, markedForTrade ? 1 : 0);
  };
  const liveCollectionCount = collection => {
    const scopedCards = cardsInCollection(collection.id);
    if (form.shareMode === "trade") return scopedCards.reduce((total, card) => total + tradeQuantity(card), 0);
    if (form.shareMode === "wanted") return scopedCards.filter(card => card.status === "wanted").length;
    return scopedCards.filter(card => card.status !== "wanted").length;
  };
  const collectionFallbackCount = collection => {
    if (form.shareMode === "trade") return Number(collection.available_copies_count || collection.trade_count || collection.duplicate_cards_count || 0);
    if (form.shareMode === "wanted") return Number(collection.wanted_count || 0);
    return Number(collection.cards_count || 0);
  };
  const collectionModeCount = collection => Math.max(liveCollectionCount(collection), collectionFallbackCount(collection));
  const shareableCollections = form.shareMode
    ? collections.filter(collection => form.shareMode === "binder" || collectionModeCount(collection) > 0)
    : [];
  const collectionModeUnit = count => {
    if (form.shareMode === "trade") return count === 1 ? "carta disponible" : "cartas disponibles";
    if (form.shareMode === "wanted") return count === 1 ? "buscada" : "buscadas";
    return count === 1 ? "carta" : "cartas";
  };

  useFeedbackMessage(notice,"success");

  return (
    <Container className="page-band feed-page">
      {isHome && !localStorage.getItem("token") ? (
        <section className="feed-welcome">
          <div>
            <span className="home-kicker">La comunidad donde las cartas se encuentran</span>
            <h1>Descubre cartas reales, binders y coleccionistas cerca de ti.</h1>
            <p>Busca la carta que te falta, muestra tu coleccion y conecta alrededor de tiendas y eventos TCG.</p>
          </div>
          <div className="feed-welcome-actions">
            <Button as={Link} to="/register" className="btn-cardora">Crear cuenta gratis</Button>
            <Button as={Link} to="/communities" variant="outline-dark">Explorar comunidades</Button>
          </div>
        </section>
      ) : (
        <>
          <h1 className="section-title">Feed</h1>
          <p className="muted-copy">Pulls, intercambios, cartas buscadas y avances de binders.</p>
        </>
      )}

      <div className="feed-filter-bar mb-3">
        {feedFilters.map(([value, label]) => (
          <Button
            key={value}
            size="sm"
            variant={typeFilter === value ? "dark" : "outline-dark"}
            onClick={() => setTypeFilter(value)}
          >
            {label}
          </Button>
        ))}
      </div>

      {localStorage.getItem("token") && <ActivationGuide />}

      {!localStorage.getItem("token") && (
        <Card className="cardora-card mb-4 cardora-join-cta">
          <Card.Body>
            <div>
              <strong>Quieres publicar, comentar o conectar?</strong>
              <span>Registrate gratis para subir pulls, buscar cartas y hablar con otros coleccionistas.</span>
            </div>
            <Button as={Link} to="/register" className="btn-cardora">Crear cuenta</Button>
          </Card.Body>
        </Card>
      )}

      {localStorage.getItem("token") && (
        <Card className="cardora-card mb-4 feed-composer">
          <Card.Body>
            <Form onSubmit={createPost}>
              <div className="feed-composer-heading">
                <span><i className="bi bi-chat-square-heart" /></span>
                <div><strong>Comparte con la comunidad</strong><small>Un pull, una carta buscada o el progreso de tu binder.</small></div>
              </div>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label>Tipo</Form.Label>
                  <Form.Select value={form.type} onChange={event => setForm({ ...form, type: event.target.value, collectionId: "", shareMode: "" })}>
                    <option value="status">Estado</option>
                    <option value="pull">Pull hecho</option>
                    <option value="trade_completed">Carta tradeada</option>
                    <option value="wanted_card">Carta buscada</option>
                    <option value="collection_update">Compartir binder</option>
                  </Form.Select>
                </Col>
                {form.type === "collection_update" && (
                  <>
                    <Col md={4}>
                      <Form.Label>Que quieres compartir</Form.Label>
                      <Form.Select required value={form.shareMode} onChange={event => setForm({ ...form, shareMode: event.target.value, collectionId: "" })}>
                        <option value="">Selecciona el contenido</option>
                        <option value="binder">Binder completo</option>
                        <option value="trade">Copias y cartas para intercambio</option>
                        <option value="wanted">Cartas buscadas</option>
                      </Form.Select>
                    </Col>
                    <Col md={4}>
                      <Form.Label>Binder</Form.Label>
                      <Form.Select required disabled={!form.shareMode || shareContentLoading} value={form.collectionId} onChange={event => setForm({ ...form, collectionId: event.target.value })}>
                        <option value="">{shareContentLoading ? "Cargando tus cartas..." : form.shareMode ? "Selecciona un binder" : "Primero elige que compartir"}</option>
                        {shareableCollections.map(collection => {
                          const count = collectionModeCount(collection);
                          return <option value={collection.id} key={collection.id}>{collection.name} - {count} {collectionModeUnit(count)}</option>;
                        })}
                      </Form.Select>
                      {form.shareMode && !shareContentLoading && !shareableCollections.length && <Form.Text>No tienes contenido de este tipo en binders publicos.</Form.Text>}
                    </Col>
                  </>
                )}
                <Col xs={12}>
                  <Form.Label>Publicacion</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={form.body}
                    onChange={event => setForm({ ...form, body: event.target.value })}
                    placeholder="Subi tu pull, cuenta que carta tradeaste o que estas buscando..."
                    required
                  />
                </Col>
              </Row>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                className="visually-hidden"
                onChange={event => {
                  const selected = Array.from(event.target.files || []).slice(0, 2 - form.images.length);
                  setForm(current => ({ ...current, images: [...current.images, ...selected].slice(0, 2) }));
                  event.target.value = "";
                }}
              />
              <div className="feed-composer-tools">
                <Button type="button" variant="outline-dark" className="feed-add-image" onClick={() => imageInputRef.current?.click()} disabled={form.images.length >= 2}>
                  <i className="bi bi-image me-2" />Añadir foto{form.images.length ? ` (${form.images.length}/2)` : ""}
                </Button>
                {form.images.length > 0 && (
                  <div className="feed-image-previews">
                    {form.images.map((file, index) => (
                      <FeedImagePreview key={`${file.name}-${file.lastModified}-${index}`} file={file} onRemove={() => setForm(current => ({ ...current, images: current.images.filter((_, imageIndex) => imageIndex !== index) }))} />
                    ))}
                  </div>
                )}
              </div>
              <Button type="submit" className="btn-cardora mt-3 feed-composer-submit" disabled={isBusy("publish")} aria-busy={isBusy("publish")}>{isBusy("publish") && <Spinner size="sm" className="me-2" />}{isBusy("publish") ? "Publicando..." : "Publicar"}</Button>
            </Form>
          </Card.Body>
        </Card>
      )}

      {feedLoading && <FeedSkeleton />}
      <div className="d-grid gap-3">
        {!feedLoading && posts.length === 0 && (
          <Card className="cardora-card cardora-empty-state">
            <Card.Body>
              <strong>Aun no hay actividad publicada.</strong>
              <span>Empieza mostrando una carta, compartiendo un binder o contando tu ultima apertura.</span>
              {localStorage.getItem("token") ? <Button as={Link} to="/binder" className="btn-cardora mt-2">Ir a mi binder</Button> : <Button as={Link} to="/register" className="btn-cardora mt-2">Crear cuenta</Button>}
            </Card.Body>
          </Card>
        )}
        {posts.map(post => (
          <Card className={`cardora-card feed-card ${postTypeClass(post.type)}`} key={post.id}>
            <Card.Body>
              <div className="feed-post-head">
                <Link className="feed-avatar" to={`/u/${post.user_id}`}>
                  {post.avatar_url ? <img src={mediaUrl(post.avatar_url)} alt="" /> : <span>{avatarInitial(post.display_name)}</span>}
                </Link>
                <div className="feed-post-meta">
                  <div className="feed-post-author-line">
                    <Link className="feed-author" to={`/u/${post.user_id}`}>{post.display_name}</Link>
                    {post.business_name && <Link className="feed-store-link" to={`/store/${post.business_slug}`}>{post.business_name}</Link>}
                  </div>
                  <time>{new Date(post.created_at).toLocaleString("es-ES")}</time>
                </div>
                <div className="feed-post-head-actions">
                  <Badge className={`feed-type-badge ${postTypeClass(post.type)}`}>{postTypes[post.type] || post.type}</Badge>
                  {localStorage.getItem("token") && currentUser?.id !== post.user_id && post.connection_status === "none" && (
                    <Button
                      type="button"
                      className="feed-connect-button"
                      variant="outline-dark"
                      size="sm"
                      disabled={isBusy(`connect:${post.user_id}`)}
                      onClick={() => connectUser(post.user_id)}
                      aria-label={`Conectar con ${post.display_name}`}
                      title={`Conectar con ${post.display_name}`}
                    >
                      {isBusy(`connect:${post.user_id}`) ? <Spinner size="sm" /> : <i className="bi bi-person-plus" />}
                    </Button>
                  )}
                  {localStorage.getItem("token") && currentUser?.id !== post.user_id && (post.connection_status === "pending_outgoing" || requestedUserIds.has(post.user_id)) && (
                    <Button type="button" className="feed-connect-button is-pending" variant="outline-dark" size="sm" disabled aria-label="Solicitud enviada" title="Solicitud enviada">
                      <i className="bi bi-person-check" />
                    </Button>
                  )}
                </div>
              </div>

              <p className="feed-post-body">{post.body}</p>

              {post.type === "collection_update" && post.metadata?.collectionId && (
                <div className="feed-binder-share">
                  <i className={post.metadata.shareMode === "duplicates" ? "bi bi-copy" : post.metadata.shareMode === "trade" ? "bi bi-arrow-left-right" : post.metadata.shareMode === "wanted" ? "bi bi-search-heart" : "bi bi-journal-album"} />
                  <div>
                    <strong>{post.metadata.collectionName || "Binder"}</strong>
                    <span>{post.metadata.themeQuery ? `Tematica: ${post.metadata.themeQuery}` : post.metadata.binderStructure === "set" ? "Binder por set" : "Binder personalizado"}</span>
                    <small>{binderShareDetail(post.metadata)}</small>
                  </div>
                </div>
              )}

              {postLinks(post).length > 0 && (
                <div className="feed-link-row">
                  {postLinks(post).map(([label, url]) => (
                    <Button as={Link} to={url} size="sm" variant="outline-dark" key={`${post.id}-${label}`}>{label}</Button>
                  ))}
                </div>
              )}

              {post.images?.length > 0 && (
                <div className={`feed-image-grid ${post.images.length === 1 ? "single" : ""}`}>
                  {[...post.images].sort((a, b) => a.sort_order - b.sort_order).map((image, index, orderedImages) => (
                    <button type="button" className="feed-image-button" key={image.id} onClick={() => { setViewerImageLoaded(false); setImageViewer({ images: orderedImages, index, post }); }} aria-label="Abrir imagen">
                      <img src={mediaUrl(image.image_url)} alt={`Publicacion de ${post.display_name}`} loading="lazy" decoding="async" />
                    </button>
                  ))}
                </div>
              )}

              <div className="feed-actions">
                <Button className="feed-action-primary" variant={likedPostIds.has(post.id) ? "dark" : "outline-dark"} size="sm" disabled={isBusy(`like:${post.id}`)} onClick={() => likePost(post.id)}>
                  {isBusy(`like:${post.id}`) ? <Spinner size="sm" className="me-1" /> : <i className={`bi ${likedPostIds.has(post.id) ? "bi-heart-fill" : "bi-heart"} me-1`} />}{post.likes_count || 0}
                </Button>
                {localStorage.getItem("token") && (
                  <Dropdown align="end" className="feed-action-menu">
                    <Dropdown.Toggle variant="outline-dark" size="sm" aria-label="Mas acciones de la publicacion">
                      <i className="bi bi-three-dots" /><span>Mas</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {currentUser?.id === post.user_id || currentUser?.role === "super_admin" ? (
                        pendingDeleteId === post.id ? (
                          <>
                            <Dropdown.ItemText>Eliminar esta publicacion?</Dropdown.ItemText>
                            <Dropdown.Item as="button" className="text-danger" disabled={deletingPostId === post.id} onClick={() => deletePost(post.id)}>
                              {deletingPostId === post.id ? <Spinner size="sm" className="me-2" /> : <i className="bi bi-trash me-2" />}
                              Si, borrar
                            </Dropdown.Item>
                            <Dropdown.Item as="button" onClick={() => setPendingDeleteId(null)}>Cancelar</Dropdown.Item>
                          </>
                        ) : (
                          <Dropdown.Item as="button" className="text-danger" onClick={() => setPendingDeleteId(post.id)}>
                            <i className="bi bi-trash me-2" />Borrar publicacion
                          </Dropdown.Item>
                        )
                      ) : (
                        <>
                          <Dropdown.Item as="button" onClick={() => openMessage(post)}><i className="bi bi-envelope me-2" />Enviar mensaje</Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item as="button" className="text-danger" disabled={reportedPostIds.has(post.id) || isBusy(`report:${post.id}`)} onClick={() => reportPost(post.id)}>
                            {isBusy(`report:${post.id}`) && <Spinner size="sm" className="me-2" />}
                            <i className="bi bi-flag me-2" />{reportedPostIds.has(post.id) ? "Reportado" : "Reportar publicacion"}
                          </Dropdown.Item>
                        </>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                )}
                <Button variant="link" size="sm" className="profile-social-count feed-action-primary feed-comment-count" disabled={!post.comments_count} onClick={() => toggleComments(post.id)} aria-label={`${post.comments_count || 0} comentarios`}>
                  <i className="bi bi-chat me-1" /><span className="feed-comment-total">{post.comments_count || 0}</span><span className="feed-comment-label"> comentarios</span>
                </Button>
              </div>

              {expandedCommentPostIds.has(post.id) && post.comments?.length > 0 && (
                <div className="feed-comments">
                  {post.comments.map(comment => (
                    <div className="feed-comment" key={comment.id}>
                      <div className="feed-comment-head">
                        <strong>{comment.display_name}</strong>
                        <time>{commentTime(comment.created_at)}</time>
                      </div>
                      <span>{comment.body}</span>
                    </div>
                  ))}
                </div>
              )}

              {localStorage.getItem("token") ? (
                <Form className="feed-comment-form" onSubmit={event => sendComment(event, post.id)}>
                  <Form.Control value={comments[post.id] || ""} onChange={event => setComments({ ...comments, [post.id]: event.target.value })} placeholder="Comentar..." />
                  <Button type="submit" variant="outline-dark" disabled={isBusy(`comment:${post.id}`)} aria-label="Enviar comentario">{isBusy(`comment:${post.id}`) ? <Spinner size="sm" /> : <i className="bi bi-send" />}</Button>
                </Form>
              ) : (
                <div className="feed-guest-comment-cta">
                  <span>Quieres comentar esta publicacion?</span>
                  <Button as={Link} to="/register" size="sm" className="btn-cardora">Crear cuenta</Button>
                </div>
              )}
            </Card.Body>
          </Card>
        ))}
      </div>

      <Modal show={Boolean(imageViewer)} onHide={() => setImageViewer(null)} centered size="lg" dialogClassName="feed-image-viewer">
        <Modal.Body>
          <div className="feed-image-stage">
            {!viewerImageLoaded && <Spinner animation="border" variant="light" />}
            {imageViewer && <img className={viewerImageLoaded ? "is-loaded" : ""} src={mediaUrl(imageViewer.images[imageViewer.index]?.image_url)} alt={`Imagen ${imageViewer.index + 1} de la publicacion`} onLoad={() => setViewerImageLoaded(true)} />}
          </div>
          {imageViewer?.images.length > 1 && <div className="feed-image-counter">{imageViewer.index + 1} / {imageViewer.images.length}</div>}
        </Modal.Body>
        <Modal.Footer className="feed-viewer-actions">
          <Button variant="outline-light" disabled={!imageViewer || imageViewer.index === 0} onClick={() => { setViewerImageLoaded(false); setImageViewer(current => ({ ...current, index: current.index - 1 })); }} aria-label="Imagen anterior"><i className="bi bi-chevron-left" /></Button>
          <Button variant="light" onClick={() => setImageViewer(null)}>Cerrar</Button>
          <Button variant="outline-light" disabled={!imageViewer || imageViewer.index >= imageViewer.images.length - 1} onClick={() => { setViewerImageLoaded(false); setImageViewer(current => ({ ...current, index: current.index + 1 })); }} aria-label="Imagen siguiente"><i className="bi bi-chevron-right" /></Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(messageTarget)} onHide={() => setMessageTarget(null)} centered>
        <Modal.Header closeButton><Modal.Title>Mensaje privado</Modal.Title></Modal.Header>
        <Form onSubmit={sendPrivateMessage}>
          <Modal.Body>
            <p className="muted-copy">Para: <strong>{messageTarget?.name}</strong></p>
            <Form.Group className="mb-3">
              <Form.Label>Asunto</Form.Label>
              <Form.Control value={privateMessage.subject} onChange={event => setPrivateMessage({ ...privateMessage, subject: event.target.value })} placeholder="Trade, carta buscada, quedada..." />
            </Form.Group>
            <Form.Group>
              <Form.Label>Mensaje</Form.Label>
              <Form.Control as="textarea" rows={4} value={privateMessage.body} onChange={event => setPrivateMessage({ ...privateMessage, body: event.target.value })} required />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-dark" onClick={() => setMessageTarget(null)}>Cerrar</Button>
            <Button type="submit" className="btn-cardora" disabled={isBusy("message")}>{isBusy("message") && <Spinner size="sm" className="me-2" />}{isBusy("message") ? "Enviando..." : "Enviar"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default FeedHome;
